import {
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@aspnet/signalr';
import { getToken } from '../ReactAdminOss._Core/src/security/tokenHelper';
import { isSessionRejected } from './chatApi';

// Повторното свързване е със случайно изчакване (първият опит — 1–20 сек., после нарастващо до 30 сек.): след рестарт
// на сървиса или на nginx всички таблети и календари иначе се връщат в една и съща секунда и натоварват базата наведнъж
// (при всички техници — до ~1 000 клиента; анализът за скалирането, 25.10.2026).
const firstRetryMinMs = 1000;
const firstRetryMaxMs = 20000;
const retryBaseMs = 5000;
const retryMaxMs = 30000;

let connection = null;
let starting = null;
let failedAttempts = 0;
let retryTimer = null;
// Докато ChatSwitch не потвърди, че чатът е включен — никакво свързване.
let suspended = true;
const reconnectListeners = new Set();
// Отвореният чат и слушането на заден план може да искат една и съща група — излизаме от нея чак когато никой не я
// държи. Хъбът губи групите с връзката — при повторно свързване влизаме пак във всички.
const joinedChats = new Map();
// Изтекла сесия: хъбът отказва (401), а свързването иначе се пробва пак на всеки ≤30 сек. без край — след рестарт на
// пода от всеки таблет с изтекла сесия (26.10.2026). Не се пробва повече, докато токенът не се смени: нов вход тук или в
// друг таб.
let sessionRejected = false;
let rejectedToken = null;

const createConnection = () => {
  const hubConnection = new HubConnectionBuilder()
    .configureLogging(LogLevel.None)
    // Браузърът не праща хедър по WebSocket — токенът отива като access_token в адреса.
    // Само WebSocket, без negotiate: резервните транспорти (SSE, long polling) държат HTTP връзка към същия хост
    // като API-то на таблета постоянно. Без WebSocket чатът работи без обновяване на живо.
    .withUrl(process.env.REACT_APP_TECHNICIAN_APP_CHAT_HUB, {
      accessTokenFactory: () => getToken(),
      transport: HttpTransportType.WebSockets,
      skipNegotiation: true,
    })
    .build();

  return hubConnection;
};

const retryDelayMs = attempt =>
  attempt === 0
    ? firstRetryMinMs + Math.random() * (firstRetryMaxMs - firstRetryMinMs)
    : Math.min(retryMaxMs, retryBaseMs * 2 ** attempt) * (0.2 + Math.random() * 0.8);

const scheduleRetry = retry => {
  if (retryTimer) return;
  const delay = retryDelayMs(failedAttempts);
  failedAttempts += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    retry().catch(() => true);
  }, delay);
};

// Браузърът не дава статуса на неуспешен WebSocket — дали сесията е изтекла, казва chat/access (401).
const retryUnlessSessionRejected = retry => {
  const token = getToken();
  isSessionRejected().then(rejected => {
    if (suspended) return;
    if (!rejected) {
      scheduleRetry(retry);
      return;
    }
    sessionRejected = true;
    rejectedToken = token;
  });
};

const startChatConnection = () => {
  if (suspended) return Promise.reject(new Error('Чатът е изключен.'));
  if (sessionRejected) {
    if (getToken() === rejectedToken) return Promise.reject(new Error('Сесията е изтекла.'));
    sessionRejected = false;
    failedAttempts = 0;
  }
  if (!connection) {
    connection = createConnection();
    // @aspnet/signalr 1.1 няма автоматично повторно свързване.
    connection.onclose(() => !suspended && scheduleRetry(startChatConnection));
  }
  if (connection.state === HubConnectionState.Connected) return Promise.resolve(connection);
  if (starting) return starting;

  starting = connection.start().then(
    () => {
      starting = null;
      // Изключен, докато се е свързвал.
      if (suspended) {
        connection.stop();
        throw new Error('Чатът е изключен.');
      }
      failedAttempts = 0;
      joinedChats.forEach((count, chatId) =>
        connection.invoke('JoinChat', chatId).catch(() => true)
      );
      reconnectListeners.forEach(listener => listener(connection));
      return connection;
    },
    error => {
      starting = null;
      if (!suspended) retryUnlessSessionRejected(startChatConnection);
      throw error;
    }
  );

  return starting;
};

// Нов вход в друг таб сменя токена — свързваме се пак.
window.addEventListener('storage', event => {
  if (
    event.key === 'OSS_Token' &&
    sessionRejected &&
    !suspended &&
    event.newValue &&
    event.newValue !== rejectedToken
  )
    startChatConnection().catch(() => true);
});

// Слушателят се вика при всяко (повторно) свързване — за да се влезе пак в групата на чата и да се дръпне пропуснатото.
const onChatConnected = listener => {
  reconnectListeners.add(listener);
  return () => reconnectListeners.delete(listener);
};

// Изключен чат (ChatSwitch): връзката се спира и не се възстановява, докато не бъде включен пак.
const setChatConnectionSuspended = value => {
  suspended = value;
  if (!value) return;
  clearTimeout(retryTimer);
  retryTimer = null;
  if (connection && connection.state === HubConnectionState.Connected) connection.stop();
};

const joinChat = chatId => {
  const count = joinedChats.get(chatId) || 0;
  joinedChats.set(chatId, count + 1);
  if (count > 0) return Promise.resolve(true);
  return startChatConnection()
    .then(c => c.invoke('JoinChat', chatId))
    .catch(() => false);
};

const leaveChat = chatId => {
  const count = joinedChats.get(chatId) || 0;
  if (count > 1) {
    joinedChats.set(chatId, count - 1);
    return;
  }
  joinedChats.delete(chatId);
  if (connection && connection.state === HubConnectionState.Connected)
    connection.invoke('LeaveChat', chatId).catch(() => true);
};

export { startChatConnection, onChatConnected, setChatConnectionSuspended, joinChat, leaveChat };
